<template>
  <div class="fu-dr" :class="`fu-dr--${layout}`" :data-layout="layout" data-test="fu-drawer">
    <div class="fu-dr__top">
      <slot name="header" :layout="layout" />
      <slot name="facts" :layout="layout" />
    </div>

    <div class="fu-dr__body">
      <div class="fu-dr__cols">
        <section class="fu-dr__card fu-dr__log" aria-labelledby="fu-dr-log-title" data-test="fu-drawer-log">
          <div class="fu-dr__log-head">
            <h3 id="fu-dr-log-title" class="fu-dr__log-title">Log a follow-up</h3>
            <div class="fu-dr__log-progress"><slot name="progress" :layout="layout" /></div>
          </div>
          <div class="fu-dr__log-body">
            <slot name="log" :layout="layout" />
          </div>
        </section>

        <section class="fu-dr__card fu-dr__side" aria-label="Patient details" data-test="fu-drawer-side">
          <slot name="tabs" :layout="layout" />
        </section>
      </div>
    </div>

    <div class="fu-dr__foot" data-test="fu-drawer-foot">
      <slot name="footer" :layout="layout" />
    </div>
  </div>
</template>

<script>
/**
 * DrawerLayout — the follow-up drawer's shell (redesign Phase 6b, 2026-10-02; the design's drawer,
 * Follow-ups.dc.html 751–1033, layout vars L.dr*).
 *
 *   header + facts on top · two cards: "Log a follow-up" (left) and the tabs (right) · footer
 *
 * ⛔ PHONE / TABLET / DESKTOP COME FROM THE DRAWER'S OWN WIDTH, never the viewport: the drawer is a
 * slide-out whose width the host sets, and it may later live in a Schedule View pane. A
 * ResizeObserver on the root (window resize where there is none) feeds queues.js layoutFor — the
 * same 640 / 900 breakpoints as the tables:
 *   desktop ≥ 900  two columns side by side; the body scrolls, header and footer stay put
 *   tablet  < 900  the two cards stack; the body scrolls
 *   phone   < 640  stacked; the WHOLE drawer scrolls and the footer is sticky (the design's drOv /
 *                  drFootPos); 2 fact columns; the lock note hides
 * Every slot gets `{ layout }`. Emits `layout` when it changes. Presentational: no store, no mixin.
 */
import { layoutFor } from '@/utils/followups/queues.js';

export default {
  name: 'DrawerLayout',
  data() {
    return { width: 1280 };
  },
  computed: {
    layout() {
      return layoutFor(this.width);
    },
  },
  watch: {
    layout(v) {
      this.$emit('layout', v);
    },
  },
  mounted() {
    this.measure();
    if (typeof window.ResizeObserver === 'function') {
      // Applied on the NEXT frame (6c): a layout switch resizes the observed element inside the
      // callback, which raises "ResizeObserver loop completed with undelivered notifications" — an
      // uncaught error captureFormLogs reports (seen opening the drawer on a 390px phone).
      this.resizeObserver = new window.ResizeObserver((entries) => {
        const e = entries && entries[0];
        const w = e && e.contentRect ? e.contentRect.width : 0;
        if (typeof window.requestAnimationFrame !== 'function') { this.applyWidth(w); return; }
        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => { this.resizeFrame = null; this.applyWidth(w); });
      });
      this.resizeObserver.observe(this.$el);
    } else {
      window.addEventListener('resize', this.measure);
    }
    this.$emit('layout', this.layout);
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeFrame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.resizeFrame);
    window.removeEventListener('resize', this.measure);
  },
  methods: {
    measure() {
      const el = this.$el;
      const w = el ? (el.getBoundingClientRect().width || el.clientWidth) : 0;
      this.applyWidth(w || window.innerWidth);
    },
    // A hidden drawer reports 0 — keep the last real width rather than flip to "phone".
    applyWidth(w) {
      const n = Math.round(Number(w));
      if (Number.isFinite(n) && n > 0) this.width = n;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-dr {
  @include fu-font-base;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  background: $fu-neutral-25;
  overflow: hidden;
}

.fu-dr__top {
  flex: none;
  padding: 18px 24px;
  background: $fu-surface;
  border-bottom: 1px solid $fu-border;
}

.fu-dr__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
}

.fu-dr__cols {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px 24px 28px;
}

.fu-dr__card {
  @include fu-card($fu-radius-lg);
  min-width: 0;
}

.fu-dr__log { flex: 1 1 380px; }
.fu-dr__side { flex: 1.3 1 440px; }

.fu-dr__log-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid $fu-border;
}

.fu-dr__log-title {
  @include fu-font-display;
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  white-space: nowrap;
  color: $fu-text;
}

.fu-dr__log-progress {
  margin-left: auto;
  min-width: 0;
}

.fu-dr__log-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 18px 18px;
}

.fu-dr__foot {
  flex: none;
  padding: 12px 24px;
  background: $fu-surface;
  border-top: 1px solid $fu-border;
}

// ---- tablet: the two cards stack -------------------------------------------------------------
.fu-dr--tablet {
  .fu-dr__top { padding: 12px 20px; }
  .fu-dr__cols { flex-direction: column; align-items: stretch; }
  .fu-dr__log, .fu-dr__side { flex: none; width: 100%; }
}

// ---- phone: the whole drawer scrolls, footer sticky ------------------------------------------
.fu-dr--phone {
  overflow-y: auto;
  overscroll-behavior: contain;

  .fu-dr__top { padding: 16px 16px 14px; }
  .fu-dr__body { flex: none; overflow: visible; }
  .fu-dr__cols { flex-direction: column; align-items: stretch; gap: 14px; padding: 14px 12px 24px; }
  .fu-dr__log, .fu-dr__side { flex: none; width: 100%; }
  .fu-dr__log-head { padding: 12px 14px; }
  .fu-dr__log-body { padding: 14px; }
  .fu-dr__foot {
    position: sticky;
    bottom: 0;
    z-index: 2;
    padding: 10px 12px;
  }
}
</style>
